import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * An anchored floating panel, opened from a button.
 *
 * The account menu in the masthead sits on this, and the confirmations on
 * destructive row actions are next. A floating panel has to get two things
 * right that an inline disclosure never thinks about: how it is dismissed (a
 * click anywhere else, Escape, focus leaving it) and where focus goes on the
 * way in and out. Both live here so that no consumer carries its own copy.
 *
 * Non-modal on purpose. The page behind stays live, which is what a menu and
 * a small confirmation both want. A modal dialog is a different thing, and
 * this app has none.
 *
 * Fixed to the viewport and rendered into document.body rather than absolute
 * beside its trigger. Every ledger sits in an `overflow-x-auto` box, and
 * overflow-x alone is enough to make the box clip and scroll vertically too,
 * so a panel opened from a row near the bottom would scroll inside the table
 * instead of floating over it. A sticky cell's stacking context would cover
 * it the same way. Outside the tree, neither can reach it.
 *
 * The register's mark menu is the other floating surface and is not built on
 * this: it anchors to a pointer position on a right click rather than to a
 * trigger. The two share the `.popover` surface in styles.css and nothing
 * else.
 */
export interface PopoverProps {
  /** `menu` for a list of commands, `dialog` for prose with buttons under it. */
  role: 'menu' | 'dialog';
  /** The panel's accessible name. Give exactly one of `label` and `labelledBy`. */
  label?: string;
  /** Id of an element inside the panel that names it, announced on open. */
  labelledBy?: string;
  /** Contents of the trigger button. */
  trigger: ReactNode;
  triggerClassName?: string;
  /** For a trigger whose contents do not say what it opens. */
  triggerLabel?: string;
  /**
   * Anything else the trigger button needs: `disabled` while a mutation is in
   * flight, `style`, `aria-pressed` on a chip. The wiring that makes it a
   * trigger is set afterwards and cannot be overridden from here.
   */
  triggerProps?: Omit<
    ComponentPropsWithoutRef<'button'>,
    'type' | 'onClick' | 'onKeyDown' | 'children'
  >;
  /**
   * Which edge of the trigger the panel lines up with. Shifted, not flipped,
   * when that would run out of the viewport.
   */
  align?: 'start' | 'end';
  panelClassName?: string;
  /**
   * Rendered only while open. `close` shuts the panel and puts focus back on
   * the trigger. Nothing in here closes on its own after a pick: a
   * confirmation that is `pending` or showing an error stays where it is.
   */
  children: (popover: { close: () => void }) => ReactNode;
}

/** What gets focus when the panel opens: the first thing that can take it. */
const FOCUSABLE =
  '[role^="menuitem"]:not(:disabled), button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** Between the trigger and the panel, and between the panel and the viewport edge. */
const GAP = 6;
const INSET = 8;

/**
 * Below the trigger, on the chosen edge, kept inside the viewport. Above the
 * trigger instead when there is no room below and there is room above: the
 * panel is fixed, so nothing past the bottom of the viewport can be scrolled
 * to.
 */
function place(trigger: DOMRect, panel: DOMRect, align: 'start' | 'end') {
  const wanted = align === 'end' ? trigger.right - panel.width : trigger.left;
  const left = Math.max(INSET, Math.min(wanted, window.innerWidth - panel.width - INSET));
  const below = trigger.bottom + GAP;
  const above = trigger.top - GAP - panel.height;
  const top = below + panel.height > window.innerHeight - INSET && above >= INSET ? above : below;
  return { left, top };
}

export function Popover({
  role,
  label,
  labelledBy,
  trigger,
  triggerClassName,
  triggerLabel,
  triggerProps,
  align = 'end',
  panelClassName,
  children,
}: PopoverProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // Whether the close that just happened should hand focus back to the
  // trigger. Escape and a pick inside do; a press outside and Tab away do
  // not, because those are putting focus somewhere else themselves.
  const [returnFocus, setReturnFocus] = useState(false);
  const [placed, setPlaced] = useState({ left: 0, top: 0 });
  const id = useId();
  const triggerId = `${id}-trigger`;
  const panelId = `${id}-panel`;

  const show = () => {
    setReturnFocus(false);
    setOpen(true);
  };
  const dismiss = useCallback(() => {
    setReturnFocus(false);
    setOpen(false);
  }, []);
  const close = useCallback(() => {
    setReturnFocus(true);
    setOpen(false);
  }, []);

  // The panel is in document.body, so "inside" is the trigger's box or the
  // panel's, and nothing in between.
  const inside = (node: unknown) =>
    node instanceof Node &&
    (wrapperRef.current?.contains(node) === true || panelRef.current?.contains(node) === true);

  // Measured rather than estimated, the way the mark menu does it. A rect with
  // no width means no layout (a test DOM) and is left alone.
  const reposition = useCallback(() => {
    const anchor = triggerRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const rect = panel.getBoundingClientRect();
    if (rect.width > 0) setPlaced(place(anchor.getBoundingClientRect(), rect, align));
  }, [align]);

  // Focus goes in on open and the panel is placed, both before paint. Focus
  // comes back to the trigger on the closes that asked for it.
  useLayoutEffect(() => {
    if (!open) {
      if (returnFocus) triggerRef.current?.focus();
      return;
    }
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    reposition();
  }, [open, returnFocus, reposition]);

  // Dismissal from outside the panel. All of these are registered on the
  // document or window in the capture phase, so they run before whatever
  // was clicked, typed at or scrolled.
  //
  // A pointer press outside closes without moving focus: the press is about
  // to put focus wherever it landed. Escape returns focus to the trigger,
  // because nothing else is about to.
  //
  // The page scrolling under a fixed panel is fine, the panel follows its
  // trigger. An inner box scrolling is not: a ledger scrolled sideways can
  // carry the trigger out of view while the panel stays, so that closes.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!inside(event.target)) dismiss();
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    const onScroll = (event: Event) => {
      if (event.target === document) reposition();
      else dismiss();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', reposition);
    };
  });

  // Focus leaving by keyboard closes the panel without pulling focus back. A
  // null relatedTarget is ignored on purpose: Safari does not focus a button
  // on click, so a pointer pick inside the panel blurs to nothing first, and
  // closing on that would unmount the item before its click ever fired. The
  // pointerdown listener above already handles a press outside.
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const to = event.relatedTarget;
    if (to instanceof Node && !inside(to)) dismiss();
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (role === 'menu' && !open && event.key === 'ArrowDown') {
      event.preventDefault();
      show();
    }
  };

  const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel) return;

    // Tab out of either end. The panel lives at the end of the document, so
    // left to itself a forward Tab from its last button would strand focus in
    // the browser chrome. Focus is put on the trigger first and the browser's
    // own Tab is then allowed to carry on from there, which lands on whatever
    // follows the trigger in the page. Backwards is simpler: the trigger is
    // the previous stop.
    if (event.key === 'Tab') {
      const focusables = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const at = document.activeElement;
      if (!event.shiftKey && at === focusables.at(-1)) {
        dismiss();
        triggerRef.current?.focus();
      } else if (event.shiftKey && at === focusables[0]) {
        event.preventDefault();
        close();
      }
      return;
    }

    // Arrow keys walk a menu's items and wrap, as they do in the mark menu.
    // A dialog is left to Tab, which is what its buttons expect.
    if (role !== 'menu') return;
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const items = [...panel.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled)')];
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (index + (event.key === 'ArrowDown' ? 1 : items.length - 1) + items.length) %
            items.length;
    items[next]?.focus();
  };

  return (
    <div ref={wrapperRef} className="inline-flex" onBlur={onBlur}>
      <button
        className={triggerClassName}
        aria-label={triggerLabel}
        {...triggerProps}
        ref={triggerRef}
        type="button"
        id={triggerId}
        aria-haspopup={role}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={open ? dismiss : show}
        onKeyDown={onTriggerKeyDown}
      >
        {trigger}
      </button>
      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role={role}
              aria-label={label}
              aria-labelledby={labelledBy}
              className={`popover fixed ${panelClassName ?? ''}`}
              style={placed}
              onKeyDown={onPanelKeyDown}
            >
              {children({ close })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
